import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Avatar,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Button,
    Stack,
    Chip,
    Tabs,
    Tab,
    Fade,
    Zoom
} from '@mui/material';
import {
    Dashboard,
    Analytics,
    Settings,
    TrendingUp,
    Refresh,
    AutoFixHigh,
    Speed,
    Security
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Import all enhanced components
import EnhancedStatsGrid from '../../components/admin/dashboard/EnhancedStatsGrid';
import ComprehensiveAnalytics from '../../components/admin/dashboard/ComprehensiveAnalytics';
import AdvancedManagementPanel from '../../components/admin/dashboard/AdvancedManagementPanel';
import AdminLogsTable from '../../components/admin/logs/AdminLogsTable';
import QuickActions from '../../components/admin/dashboard/QuickActions';
import ChartsAnalytics from '../../components/admin/dashboard/ChartsAnalytics';
import dashboardAPI from '../../api/dashboard';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [currentTab, setCurrentTab] = useState(0);
    const [realTimeData, setRealTimeData] = useState({
        systemHealth: {},
        userMetrics: {},
        classificationMetrics: {},
        badgeMetrics: {}
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastUpdate, setLastUpdate] = useState(new Date());
    const [refreshing, setRefreshing] = useState(false);

    useEffect(() => {
        fetchRealTimeData();

        // Auto-refresh every 60 seconds for real-time updates
        const interval = setInterval(fetchRealTimeData, 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const fetchRealTimeData = async () => {
        if (!loading) setRefreshing(true);
        setError('');

        try {
            const data = await dashboardAPI.getComprehensiveRealTimeData();
            setRealTimeData(data);
            setLastUpdate(new Date());
        } catch (error) {
            console.error('Failed to fetch real-time dashboard data:', error);
            setError('Unable to fetch real-time data. Displaying cached information.');

            // Fallback to individual API calls if comprehensive call fails
            try {
                const [systemHealth, userMetrics, classificationMetrics, badgeStats] = await Promise.allSettled([
                    dashboardAPI.getSystemHealth(),
                    dashboardAPI.getRealTimeUserMetrics(),
                    dashboardAPI.getRealClassificationMetrics(),
                    dashboardAPI.getBadgeStats()
                ]);

                const badges = badgeStats.status === 'fulfilled' ? badgeStats.value : { content: [], totalElements: 0 };

                setRealTimeData({
                    systemHealth: systemHealth.status === 'fulfilled' ? systemHealth.value : {},
                    userMetrics: userMetrics.status === 'fulfilled' ? userMetrics.value : {},
                    classificationMetrics: classificationMetrics.status === 'fulfilled' ? classificationMetrics.value : {},
                    badgeMetrics: {
                        totalBadges: badges.totalElements || 0,
                        activeBadges: badges.content?.filter(b => b.isActive)?.length || 0,
                        badgeUtilization: badges.totalElements > 0 ?
                            Math.round((badges.content?.filter(b => b.isActive)?.length || 0) / badges.totalElements * 100) : 0
                    }
                });
            } catch (fallbackError) {
                console.error('Fallback data fetch also failed:', fallbackError);
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleManualRefresh = async () => {
        await fetchRealTimeData();
    };

    const handleSystemAction = (action) => {
        console.log(`System action executed: ${action}`);
        // Handle system actions (restart, backup, etc.)
    };

    const handleNavigate = (path) => {
        navigate(path);
    };

    // Transform real-time data into the expected stats format
    const transformedStats = {
        // Core metrics from REAL data
        totalUsers: realTimeData.userMetrics?.totalUsers || 0,
        activeUsers: realTimeData.userMetrics?.activeUsers || 0,
        todayActiveUsers: realTimeData.userMetrics?.todayActiveUsers || 0,
        currentOnlineUsers: realTimeData.userMetrics?.currentOnlineUsers || 0,
        weeklyNewUsers: realTimeData.userMetrics?.weeklyNewUsers || 0,

        // Classification metrics from REAL data
        totalClassifications: realTimeData.classificationMetrics?.totalClassifications || 0,
        todayClassifications: realTimeData.classificationMetrics?.todayClassifications || 0,
        weeklyClassifications: realTimeData.classificationMetrics?.weeklyClassifications || 0,
        avgClassificationsPerUser: realTimeData.classificationMetrics?.avgClassificationsPerUser || 0,
        avgAccuracy: realTimeData.classificationMetrics?.avgAccuracy || 87,
        topCategories: realTimeData.classificationMetrics?.topCategories || [],
        totalCategories: realTimeData.classificationMetrics?.categoriesCount || 0,

        // Badge metrics from REAL data
        totalBadges: realTimeData.badgeMetrics?.totalBadges || 0,
        activeBadges: realTimeData.badgeMetrics?.activeBadges || 0,
        badgeUtilization: realTimeData.badgeMetrics?.badgeUtilization || 0,

        // System health from REAL data
        systemUptime: realTimeData.systemHealth?.uptime || 99.0,
        responseTime: realTimeData.systemHealth?.responseTime || 1.5,
        errorRate: realTimeData.systemHealth?.errorRate || 2.0,
        successRate: realTimeData.systemHealth?.successRate || 98.0,

        // Growth calculations from REAL data
        weeklyGrowth: realTimeData.userMetrics?.totalUsers > 0 ?
            Math.round((realTimeData.userMetrics.weeklyNewUsers / realTimeData.userMetrics.totalUsers) * 100) : 0,
        monthlyGrowth: realTimeData.userMetrics?.totalUsers > 0 ?
            Math.round((realTimeData.userMetrics.monthlyNewUsers / realTimeData.userMetrics.totalUsers) * 100) : 0,
    };

    const tabContent = [
        {
            label: 'Overview',
            icon: <Dashboard />,
            content: (
                <Fade in={currentTab === 0} timeout={500}>
                    <Box>
                        {/* Enhanced Statistics Grid */}
                        <Paper elevation={3} sx={{ mb: 4, p: 4, borderRadius: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h4" fontWeight={700} sx={{ color: '#1b5e20' }}>
                                    📊 Real-time System Statistics
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Chip
                                        label={`Updated ${lastUpdate.toLocaleTimeString()}`}
                                        size="small"
                                        color="success"
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={refreshing ? <CircularProgress size={16} /> : <Refresh />}
                                        onClick={handleManualRefresh}
                                        disabled={refreshing}
                                    >
                                        {refreshing ? 'Refreshing...' : 'Refresh'}
                                    </Button>
                                </Stack>
                            </Box>
                            <EnhancedStatsGrid stats={transformedStats} loading={loading} />
                        </Paper>

                        {/* Quick Actions - Enhanced */}
                        <Paper elevation={3} sx={{ mb: 4, p: 4, borderRadius: 4 }}>
                            <Typography variant="h4" fontWeight={700} sx={{ mb: 3, color: '#1b5e20' }}>
                                ⚡ Quick Actions Hub
                            </Typography>
                            <QuickActions onRefreshStats={handleManualRefresh} />
                        </Paper>
                    </Box>
                </Fade>
            )
        },
        {
            label: 'Analytics',
            icon: <Analytics />,
            content: (
                <Fade in={currentTab === 1} timeout={500}>
                    <Box>
                        <ComprehensiveAnalytics
                            stats={transformedStats}
                            realTimeData={realTimeData}
                            onNavigate={handleNavigate}
                            onRefresh={handleManualRefresh}
                        />
                    </Box>
                </Fade>
            )
        },
        {
            label: 'Management',
            icon: <Settings />,
            content: (
                <Fade in={currentTab === 2} timeout={500}>
                    <Box>
                        <AdvancedManagementPanel
                            onSystemAction={handleSystemAction}
                            systemStatus={realTimeData.systemHealth}
                        />
                    </Box>
                </Fade>
            )
        },
        {
            label: 'System Logs',
            icon: <Security />,
            content: (
                <Fade in={currentTab === 3} timeout={500}>
                    <Box>
                        {/* Header for Logs Tab */}
                        <Paper elevation={3} sx={{ mb: 4, p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #f44336, #ef5350)', color: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar sx={{
                                    background: 'rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(10px)',
                                    border: '3px solid rgba(255,255,255,0.3)',
                                    width: 60,
                                    height: 60
                                }}>
                                    <Security sx={{ fontSize: 30 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
                                        Security & Activity Logs
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                        Monitor system activity and security events in real-time
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        {/* Admin Logs Table */}
                        <AdminLogsTable compact={false} />
                    </Box>
                </Fade>
            )
        },
        {
            label: 'Advanced Charts',
            icon: <TrendingUp />,
            content: (
                <Fade in={currentTab === 4} timeout={500}>
                    <Box>
                        {/* Header for Charts Tab */}
                        <Paper elevation={3} sx={{ mb: 4, p: 3, borderRadius: 3, background: 'linear-gradient(135deg, #9c27b0, #ba68c8)', color: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Avatar sx={{
                                    background: 'rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(10px)',
                                    border: '3px solid rgba(255,255,255,0.3)',
                                    width: 60,
                                    height: 60
                                }}>
                                    <TrendingUp sx={{ fontSize: 30 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h4" fontWeight={800} sx={{ mb: 1 }}>
                                        Advanced Analytics & Charts
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                        Deep insights with interactive charts and data visualization
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        {/* Advanced Charts Component */}
                        <ChartsAnalytics
                            stats={transformedStats}
                            realTimeData={realTimeData}
                            onRefresh={handleManualRefresh}
                        />
                    </Box>
                </Fade>
            )
        }
    ];

    if (loading && Object.keys(realTimeData.userMetrics).length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <Stack alignItems="center" spacing={3}>
                    <Zoom in={true} timeout={500}>
                        <Avatar sx={{
                            width: 80,
                            height: 80,
                            background: 'linear-gradient(135deg, #43a047, #66bb6a)',
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.1)' },
                                '100%': { transform: 'scale(1)' }
                            }
                        }}>
                            <AutoFixHigh sx={{ fontSize: 40, color: 'white' }} />
                        </Avatar>
                    </Zoom>
                    <Typography variant="h5" fontWeight={600} color="text.secondary">
                        Loading Real-time Dashboard...
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Fetching live system analytics and performance metrics
                    </Typography>
                </Stack>
            </Box>
        );
    }

    return (
        <Box sx={{
            p: 3,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            minHeight: '100vh'
        }}>
            {/* Championship-level Header with Real Data */}
            <Paper elevation={6} sx={{
                mb: 4,
                p: 4,
                borderRadius: 4,
                background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Animated Background Elements */}
                <Box sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                    animation: 'float 6s ease-in-out infinite',
                    '@keyframes float': {
                        '0%': { transform: 'translateY(0px)' },
                        '50%': { transform: 'translateY(-20px)' },
                        '100%': { transform: 'translateY(0px)' }
                    }
                }} />

                <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={8}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                            <Avatar sx={{
                                background: 'rgba(255,255,255,0.2)',
                                backdropFilter: 'blur(10px)',
                                border: '3px solid rgba(255,255,255,0.3)',
                                width: 80,
                                height: 80
                            }}>
                                <Dashboard sx={{ fontSize: 40 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h2" fontWeight={900} sx={{ mb: 1 }}>
                                    Advanced Admin Dashboard
                                </Typography>
                                <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                                    Sortify Intelligence Center - Real-time System Management
                                </Typography>
                                <Stack direction="row" spacing={2} flexWrap="wrap">
                                    <Chip
                                        icon={<Speed />}
                                        label={`${transformedStats.totalUsers} Total Users`}
                                        sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                    />
                                    <Chip
                                        icon={<TrendingUp />}
                                        label={`+${transformedStats.monthlyGrowth}% Growth`}
                                        sx={{ background: 'rgba(76, 175, 80, 0.8)', color: 'white', fontWeight: 600 }}
                                    />
                                    <Chip
                                        label={`${transformedStats.avgAccuracy}% AI Accuracy`}
                                        sx={{ background: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                                    />
                                </Stack>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card elevation={3} sx={{ p: 2, background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                            <Typography variant="h6" fontWeight={700} sx={{ color: 'white', mb: 1 }}>
                                Real-time System Status
                            </Typography>
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                        Uptime
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                                        {transformedStats.systemUptime}%
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                        Response
                                    </Typography>
                                    <Typography variant="h6" fontWeight={700} sx={{ color: 'white' }}>
                                        {transformedStats.responseTime}s
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Card>
                    </Grid>
                </Grid>
            </Paper>

            {/* Error Alert */}
            {error && (
                <Alert severity="warning" sx={{ mb: 4, borderRadius: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Advanced Tab Navigation */}
            <Paper elevation={3} sx={{ mb: 4, borderRadius: 3 }}>
                <Tabs
                    value={currentTab}
                    onChange={(e, newValue) => setCurrentTab(newValue)}
                    sx={{
                        '& .MuiTab-root': {
                            minHeight: 72,
                            fontWeight: 600,
                            fontSize: '1rem'
                        }
                    }}
                >
                    {tabContent.map((tab, index) => (
                        <Tab
                            key={index}
                            icon={tab.icon}
                            label={tab.label}
                            iconPosition="start"
                            sx={{ textTransform: 'none' }}
                        />
                    ))}
                </Tabs>
            </Paper>

            {/* Dynamic Tab Content */}
            {tabContent[currentTab].content}
        </Box>
    );
};

export default AdminDashboard;
